/**
 * PDF を画面の中で紙として並べる・刷るための補助（R13 の紙の画面から切り出した。#223 の送付状でも使う）。
 *   renderPdf：PDF を 1ページずつ画像（JPEG の data URL）にする（pdf.js は使うときだけ読み込む。約 1MB）
 *   portraitForPrint：縦と横の混じった PDF（送付状は A4 縦・同封の見積書が A4 横）を、縦の紙に刷るために横のページを 90° 回した画像にする
 *     （印刷の @page は1つの向きしか持てない。回さないと横の紙が縦の紙の中で小さく刷られる）
 *   downloadBlob：Blob をこの端末に保存する（<a download> を押すのと同じ）
 */
export type PageImg = { url: string; w: number; h: number };

/** scale：72dpi の何倍で描くか（既定 3＝約 216dpi＝印刷にも使える。束ねの画面で同封書類を見るだけなら 1.5） */
export async function renderPdf(buf: ArrayBuffer, onPage: (p: PageImg, i: number, n: number) => void, opts: { scale?: number } = {}): Promise<void> {
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();
  const doc = await pdfjs.getDocument({ data: buf }).promise;
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const vp = page.getViewport({ scale: opts.scale ?? 3 }); // 72dpi × 3 ≒ 216dpi。印刷しても文字がにじまない
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(vp.width);
    canvas.height = Math.ceil(vp.height);
    const ctx = canvas.getContext("2d")!;
    await page.render({ canvasContext: ctx, viewport: vp, canvas }).promise;
    onPage({ url: canvas.toDataURL("image/jpeg", 0.92), w: vp.width, h: vp.height }, i, doc.numPages);
    page.cleanup();
  }
  await doc.destroy();
}

/** 横のページ（幅＞高さ）を 90° 回して縦の画像に。縦のページはそのまま */
export async function portraitForPrint(pages: PageImg[]): Promise<PageImg[]> {
  const out: PageImg[] = [];
  for (const p of pages) {
    if (p.w <= p.h) {
      out.push(p);
      continue;
    }
    const img = new Image();
    img.src = p.url;
    await img.decode();
    const c = document.createElement("canvas");
    c.width = Math.round(p.h);
    c.height = Math.round(p.w);
    const g = c.getContext("2d")!;
    g.translate(c.width, 0);
    g.rotate(Math.PI / 2);
    g.drawImage(img, 0, 0, p.w, p.h);
    out.push({ url: c.toDataURL("image/jpeg", 0.92), w: c.width, h: c.height });
  }
  return out;
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
